/**
 * Admin Settings Module
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded } from '../ui/dialogs.js';

interface SettingMeta {
  type: 'string' | 'boolean' | 'password' | 'int' | 'select';
  label: string;
  description?: string;
  optional?: boolean;
  readonly?: boolean;
  min?: number;
  max?: number;
  pattern?: string;
  placeholder?: string;
  options?: string[];
}

interface SettingGroup {
  label: string;
  settings: Record<string, SettingMeta>;
}

interface SettingKey {
  key: string;
  type: string;
  optional: boolean;
  readonly: boolean;
  meta: SettingMeta;
}

/** Keys that belong to the performance profile (synced with presets) */
const PERF_TUNABLE_KEYS = [
  'cpu_governor', 'cpu_epp', 'cpu_boost', 'thp_enabled', 'thp_defrag',
  'vm_swappiness', 'vm_dirty_ratio', 'vm_dirty_background_ratio',
  'vm_dirty_expire_centisecs', 'io_scheduler',
  'ksm_enabled', 'ksm_pages_to_scan', 'ksm_sleep_millisecs',
  'ksm_use_zero_pages', 'ksm_merge_across_nodes',
];

function generateAdminSettingInput(
  key: string,
  meta: SettingMeta,
  value: unknown
): string {
  const type = meta.type;
  const label = meta.label;
  const description = meta.description || '';
  const isOptional = meta.optional || false;
  const isReadonly = meta.readonly || false;
  const isEnabled = !isOptional || (value !== null && value !== undefined && value !== '');
  const disabledAttr = (!isEnabled || isReadonly) ? 'disabled' : '';

  // Build help icon if description exists
  const helpIcon = description
    ? `<span class="help-icon">?<span class="help-text">${escapeHtml(description)}</span></span>`
    : '';

  // Build optional toggle checkbox (not for readonly settings)
  const optionalToggle = (isOptional && !isReadonly)
    ? `<input type="checkbox" class="optional-toggle" data-key="${escapeAttr(key)}" ${isEnabled ? 'checked' : ''}>`
    : '';

  // Extra attributes for validation
  const attrs: string[] = [];
  if (meta.min !== undefined) attrs.push(`min="${meta.min}"`);
  if (meta.max !== undefined) attrs.push(`max="${meta.max}"`);
  if (meta.pattern) attrs.push(`pattern="${escapeAttr(meta.pattern)}"`);
  if (meta.placeholder) attrs.push(`placeholder="${escapeAttr(meta.placeholder)}"`);
  const extraAttrs = attrs.join(' ');

  let inputHtml: string;
  if (isReadonly && type !== 'boolean') {
    // Readonly non-boolean: display as plain text
    inputHtml = `<span class="setting-value" id="${escapeAttr(key)}">${escapeHtml(String(value ?? ''))}</span>`;
  } else if (type === 'boolean') {
    inputHtml = `<input type="checkbox" id="${escapeAttr(key)}" name="${escapeAttr(key)}" ${value ? 'checked' : ''} ${disabledAttr}>`;
  } else if (type === 'select') {
    const options = meta.options || [];
    let opts = '<option value="">(none)</option>';
    for (const opt of options) {
      const selected = opt === String(value || '') ? 'selected' : '';
      opts += `<option value="${escapeAttr(opt)}" ${selected}>${escapeHtml(opt)}</option>`;
    }
    inputHtml = `<select id="${escapeAttr(key)}" name="${escapeAttr(key)}" ${disabledAttr}>${opts}</select>`;
  } else if (type === 'password') {
    inputHtml = `<input type="password" id="${escapeAttr(key)}" name="${escapeAttr(key)}" value="${escapeAttr(String(value || ''))}" autocomplete="new-password" ${disabledAttr} ${extraAttrs}>`;
  } else if (type === 'int') {
    inputHtml = `<input type="number" id="${escapeAttr(key)}" name="${escapeAttr(key)}" value="${escapeAttr(String(value ?? ''))}" ${disabledAttr} ${extraAttrs}>`;
  } else {
    inputHtml = `<input type="text" id="${escapeAttr(key)}" name="${escapeAttr(key)}" value="${escapeAttr(String(value || ''))}" ${disabledAttr} ${extraAttrs}>`;
  }

  const rowClass = isReadonly ? 'setting-row setting-readonly' : 'setting-row';
  return `<div class="${rowClass}">
    <label for="${escapeAttr(key)}">${escapeHtml(label)}${helpIcon}</label>
    ${optionalToggle}
    ${inputHtml}
  </div>`;
}

function toggleOptionalSetting(key: string, checkbox: HTMLInputElement): void {
  const input = document.getElementById(key) as HTMLInputElement | null;
  if (input) {
    input.disabled = !checkbox.checked;
    if (!checkbox.checked) {
      if (input.type === 'checkbox') {
        input.checked = false;
      } else {
        input.value = '';
      }
    }
  }
}

/** Get the current value of a tunable field from the DOM */
function getTunableValue(key: string, settingKeys: SettingKey[]): unknown {
  const sk = settingKeys.find(s => s.key === key);
  if (!sk) return undefined;
  const el = document.getElementById(key) as HTMLInputElement | HTMLSelectElement | null;
  if (!el) return undefined;
  if (sk.type === 'boolean') return (el as HTMLInputElement).checked;
  if (sk.type === 'int') {
    const v = (el as HTMLInputElement).value;
    return v === '' ? null : parseInt(v);
  }
  return (el as HTMLSelectElement).value;
}

/** Set a tunable field value in the DOM */
function setTunableValue(key: string, value: unknown, settingKeys: SettingKey[]): void {
  const sk = settingKeys.find(s => s.key === key);
  if (!sk) return;
  const el = document.getElementById(key) as HTMLInputElement | HTMLSelectElement | null;
  if (!el) return;
  if (sk.type === 'boolean') {
    (el as HTMLInputElement).checked = !!value;
  } else if (sk.type === 'int') {
    (el as HTMLInputElement).value = value != null ? String(value) : '';
  } else {
    (el as HTMLSelectElement).value = String(value ?? '');
  }
}

/** Compare current tunable values against presets and return matching preset name or 'custom' */
function detectMatchingPreset(
  presets: Record<string, Record<string, unknown>>,
  settingKeys: SettingKey[],
): string {
  for (const [name, preset] of Object.entries(presets)) {
    let matches = true;
    for (const key of PERF_TUNABLE_KEYS) {
      if (!(key in preset)) continue;
      const current = getTunableValue(key, settingKeys);
      const expected = preset[key];
      // Skip comparison if the field doesn't exist in the DOM (capability not available)
      if (current === undefined) continue;
      // eslint-disable-next-line eqeqeq
      if (current != expected) {
        matches = false;
        break;
      }
    }
    if (matches) return name;
  }
  return 'custom';
}

/**
 * Open admin settings dialog
 */
export async function adminSettings(): Promise<void> {
  declareLoading();
  const response = await apiCall(api.getAdminSettings(), 'Failed to Load Settings');
  declareLoaded();
  if (!response) return;

  {

    const schema = response.schema as Record<string, SettingGroup>;
    const values = response.values as Record<string, unknown>;
    const presets = response.presets || {};

    let form = '';
    const settingKeys: SettingKey[] = [];

    for (const [, group] of Object.entries(schema)) {
      form += `<fieldset><legend>${escapeHtml(group.label)}</legend>`;
      for (const [key, meta] of Object.entries(group.settings)) {
        settingKeys.push({
          key, type: meta.type, optional: meta.optional || false,
          readonly: meta.readonly || false, meta,
        });
        form += generateAdminSettingInput(key, meta, values[key]);
      }
      form += '</fieldset>';
    }

    $('<div></div>')
      .appendTo('body')
      .html('<div><h4>Admin Settings</h4>' + form + '</div>')
      .dialog({
        modal: true,
        title: 'Admin Settings',
        zIndex: 10000,
        autoOpen: true,
        width: 600,
        maxHeight: $(window).height()! * 0.8,
        resizable: true,
        buttons: {
          Save: async function () {
            const settings: Record<string, unknown> = {};
            const errors: string[] = [];
            settingKeys.forEach(({ key, type, optional, readonly: ro, meta }) => {
              if (ro) return; // Never send readonly fields
              const input = document.getElementById(key) as HTMLInputElement | null;
              // If optional and disabled, send null to clear the setting
              if (optional && input && input.disabled) {
                settings[key] = null;
              } else if (type === 'boolean') {
                settings[key] = $('#' + key).is(':checked');
              } else if (type === 'int') {
                const val = $('#' + key).val() as string;
                if (val === '') {
                  settings[key] = null;
                } else {
                  const n = parseInt(val);
                  if (isNaN(n)) {
                    errors.push(`${meta.label}: must be a number`);
                  } else {
                    if (meta.min !== undefined && n < meta.min)
                      errors.push(`${meta.label}: minimum is ${meta.min}`);
                    if (meta.max !== undefined && n > meta.max)
                      errors.push(`${meta.label}: maximum is ${meta.max}`);
                    settings[key] = n;
                  }
                }
              } else {
                const val = $('#' + key).val() as string;
                if (meta.pattern && val && !new RegExp(meta.pattern).test(val))
                  errors.push(`${meta.label}: invalid format`);
                settings[key] = val;
              }
            });
            if (errors.length > 0) {
              alert(errors.join('\n'));
              return;
            }
            declareLoading();
            const result = await apiCall(api.setAdminSettings({ settings }), 'Failed to Save Settings');
            declareLoaded();
            if (result !== null) $(this).dialog('close');
          },
          Cancel: function () {
            $(this).dialog('close');
          },
        },
        close: function () {
          $(this).remove();
        },
        open: function () {
          // Bind optional toggle handlers
          $('.optional-toggle').on('change', function () {
            const key = $(this).data('key') as string;
            toggleOptionalSetting(key, this as HTMLInputElement);
          });

          // Profile preset handler: fill all tunables when a preset is selected
          const profileSelect = document.getElementById('perf_profile') as HTMLSelectElement | null;
          if (profileSelect) {
            profileSelect.addEventListener('change', () => {
              const selected = profileSelect.value;
              if (selected && selected !== 'custom' && presets[selected]) {
                const preset = presets[selected];
                for (const key of PERF_TUNABLE_KEYS) {
                  if (key in preset) {
                    setTunableValue(key, preset[key], settingKeys);
                  }
                }
              }
            });
          }

          // Individual tunable change: auto-detect if values match a preset
          for (const key of PERF_TUNABLE_KEYS) {
            const el = document.getElementById(key);
            if (!el || !profileSelect) continue;
            el.addEventListener('change', () => {
              const matched = detectMatchingPreset(presets, settingKeys);
              profileSelect.value = matched;
            });
          }
        },
      });
  }
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).adminSettings = adminSettings;
  (window as unknown as Record<string, unknown>).toggleOptionalSetting = toggleOptionalSetting;
}
